import { tidyText } from './titleCase';

export interface OneMapAddress {
  BLK_NO: string;
  ROAD_NAME: string;
  BUILDING: string;
  POSTAL: string;
  /** Optional — populated when returnGeom=Y. */
  LATITUDE?: string;
  LONGITUDE?: string;
}

export interface OneMapResponse {
  found: number;
  totalNumPages: number;
  pageNum: number;
  results: OneMapAddress[];
}

/**
 * Matches a BUILDING value that is actually a registered business/tenant name
 * rather than the building/development name — e.g. "MINDCHAMPS PRESCHOOL @ YISHUN
 * PTE. LTD.". OneMap indexes each tenant at a commercial/industrial postal code
 * as its own record, so a shared postal code returns several company-named
 * results alongside the one real building name.
 */
const COMPANY_NAME_PATTERN = /\b(PTE\.?\s*LTD\.?|LLP|CO\.?\s*LTD\.?|LIMITED)\b/i;

export function looksLikeCompanyName(building: string): boolean {
  return COMPANY_NAME_PATTERN.test(building);
}

/**
 * Chooses the best OneMap result for a postal code. When a postal code is shared
 * by multiple registered businesses (common for industrial/commercial buildings),
 * OneMap returns one record per tenant plus the building itself, and the first
 * result is frequently a tenant company name, not the building. We prefer the
 * first result whose BUILDING is a genuine building/development name (not a
 * company name and not the "NIL" placeholder), falling back to results[0] when
 * none qualifies (e.g. landed housing where BUILDING is "NIL").
 */
export function selectBestAddress(results: OneMapAddress[]): OneMapAddress | null {
  if (results.length === 0) return null;
  const buildingLike = results.find(
    (r) => r.BUILDING && r.BUILDING !== 'NIL' && !looksLikeCompanyName(r.BUILDING),
  );
  return buildingLike ?? results[0];
}

/**
 * Fetches address details from Singapore's official OneMap API based on a 6-digit postal code.
 * Replaces the heavy 5.1MB local postal.js payload.
 */
export async function lookupPostalCode(
  postalCode: string,
  signal?: AbortSignal,
): Promise<OneMapAddress | null> {
  const cleaned = postalCode.replace(/\D/g, '');
  if (cleaned.length !== 6) return null;

  try {
    const response = await fetch(
      `https://www.onemap.gov.sg/api/common/elastic/search?searchVal=${cleaned}&returnGeom=N&getAddrDetails=Y&pageNum=1`,
      { signal },
    );

    if (!response.ok) {
      throw new Error(`OneMap API HTTP error: ${response.status}`);
    }

    const data = (await response.json()) as OneMapResponse;

    if (data.found > 0 && data.results.length > 0) {
      const best = selectBestAddress(data.results);
      return best ? titleCaseAddress(best) : null;
    }

    return null;
  } catch (error) {
    // AbortError is the caller cancelling — don't log it as a failure.
    if ((error as Error)?.name === 'AbortError') return null;
    console.error('Failed to query OneMap API:', error);
    return null;
  }
}

/**
 * OneMap returns every text field in ALL CAPS. Every form that fills an
 * address from the lookup stores the result verbatim (quote, customer, vendor),
 * so the casing is fixed once, here, at retrieval (Ben, 2026-09-09):
 * ROAD_NAME and BUILDING to house title case, BLK_NO uppercased (`451b` →
 * `451B`), everything trimmed.
 *
 * The `NIL` sentinel OneMap uses for landed housing with no building is
 * preserved EXACTLY — every consumer drops it with `BUILDING !== 'NIL'`, and
 * `Nil` would sail past that check into the stored address.
 */
export function titleCaseAddress<
  T extends Pick<OneMapAddress, 'BLK_NO' | 'ROAD_NAME' | 'BUILDING'>,
>(address: T): T {
  return {
    ...address,
    BLK_NO: (address.BLK_NO ?? '').trim().toUpperCase(),
    ROAD_NAME: tidyText(address.ROAD_NAME),
    BUILDING: address.BUILDING === 'NIL' ? 'NIL' : tidyText(address.BUILDING),
  };
}

/**
 * Geocodes a 6-digit postal code to lat/lng coordinates via OneMap.
 * Returns null if the postal code can't be resolved.
 */
export async function geocodePostal(
  postalCode: string,
  signal?: AbortSignal,
): Promise<{ lat: number; lng: number } | null> {
  const cleaned = postalCode.replace(/\D/g, '');
  if (cleaned.length !== 6) return null;
  try {
    const response = await fetch(
      `https://www.onemap.gov.sg/api/common/elastic/search?searchVal=${cleaned}&returnGeom=Y&getAddrDetails=Y&pageNum=1`,
      { signal },
    );
    if (!response.ok) return null;
    const data = (await response.json()) as OneMapResponse;
    if (data.found > 0 && data.results.length > 0) {
      const r = selectBestAddress(data.results) ?? data.results[0];
      const lat = r.LATITUDE ? parseFloat(r.LATITUDE) : NaN;
      const lng = r.LONGITUDE ? parseFloat(r.LONGITUDE) : NaN;
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return { lat, lng };
      }
    }
    return null;
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') return null;
    console.error('Failed to geocode postal:', error);
    return null;
  }
}
